import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { useSelector } from 'react-redux';
import {
    Box,
    IconButton,
    Paper,
    Tooltip,
    Typography,
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CloseIcon from '@mui/icons-material/Close';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { fetchHubWidgetTicket } from '../../../api/customer/hub-widget.api';
import { selectCustomersidname } from '../../../store/customers/customersSlice';
import JSDB from 'fromKotlin/nk';
import type { WhatsAppConfig } from '../../../hooks/useWhatsAppConfig';

type DisplayMode = 'drawer' | 'mini';

const DISPLAY_MODE_KEY = 'hub-inbox-display-mode';
const MINI_W_KEY = 'hub-inbox-mini-w';
const MINI_H_KEY = 'hub-inbox-mini-h';
const DRAWER_W_KEY = 'hub-inbox-drawer-w';
const MINI_DEFAULT_W = 420;
const MINI_DEFAULT_H = 620;
const MINI_MIN_W = 320;
const MINI_MAX_W = 640;
const MINI_MIN_H = 400;
const MINI_MAX_H = 860;
const DRAWER_DEFAULT_W = 720;
const DRAWER_MIN_W = 380;
const DRAWER_MAX_W = 960;

function getStoredDisplayMode(): DisplayMode {
    try {
        const value = localStorage.getItem(DISPLAY_MODE_KEY);
        if (value === 'mini' || value === 'drawer') return value;
    } catch { /* ignore */ }
    return 'mini';
}

function getStoredMiniSize(): { w: number; h: number } {
    try {
        const w = parseInt(localStorage.getItem(MINI_W_KEY) || '', 10);
        const h = parseInt(localStorage.getItem(MINI_H_KEY) || '', 10);
        return {
            w: w >= MINI_MIN_W && w <= MINI_MAX_W ? w : MINI_DEFAULT_W,
            h: h >= MINI_MIN_H && h <= MINI_MAX_H ? h : MINI_DEFAULT_H,
        };
    } catch {
        return { w: MINI_DEFAULT_W, h: MINI_DEFAULT_H };
    }
}

function getStoredDrawerWidth(): number {
    try {
        const w = parseInt(localStorage.getItem(DRAWER_W_KEY) || '', 10);
        if (w >= DRAWER_MIN_W && w <= DRAWER_MAX_W) return w;
    } catch { /* ignore */ }
    return DRAWER_DEFAULT_W;
}

function erpPhone(raw: unknown): string {
    try {
        return String(JSDB().getValidPhone(String(raw || '')) || raw || '').replace(/\D/g, '');
    } catch {
        return String(raw || '').replace(/\D/g, '');
    }
}

type Props = {
    waConfig: WhatsAppConfig;
    openSignal?: number;
};

/**
 * Hub conversation widget for Official WhatsApp (server 12).
 * ERP document / pattern sends stay in sendMessage.tsx — this panel is inbox only.
 */
export default function HubInboxDrawer({ waConfig, openSignal = 0 }: Props) {
    const [open, setOpen] = useState(false);
    const [displayMode, setDisplayMode] = useState<DisplayMode>(getStoredDisplayMode);
    const [miniSize, setMiniSize] = useState(getStoredMiniSize);
    const [drawerWidth, setDrawerWidth] = useState(getStoredDrawerWidth);
    const [iframeSrc, setIframeSrc] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [unread, setUnread] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const loadingRef = useRef(false);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const customers = useSelector(selectCustomersidname);

    const appUrl = useMemo(
        () => `${String(waConfig.baseUrl || '').replace(/\/+$/, '')}/app`,
        [waConfig.baseUrl],
    );

    const clients = useMemo(
        () =>
            (customers || []).flatMap((customer: any) => {
                const id = String(customer?.id || '').trim();
                const phone = erpPhone(customer?.phone);
                if (!id || phone.length < 8) return [];
                return [{ id, name: String(customer?.name || '').trim() || phone, phone }];
            }),
        [customers],
    );

    const postClients = useCallback(() => {
        iframeRef.current?.contentWindow?.postMessage(
            { type: 'hub-widget-clients', clients },
            '*',
        );
    }, [clients]);

    const ensureSession = useCallback(async () => {
        if (iframeSrc || loadingRef.current) return;
        loadingRef.current = true;
        setError(null);
        try {
            const ticket = await fetchHubWidgetTicket(waConfig.instanceId);
            setIframeSrc(ticket.embed_url);
        } catch (e: any) {
            setError(e?.message || 'לא ניתן לפתוח שיחות');
        } finally {
            loadingRef.current = false;
        }
    }, [iframeSrc, waConfig.instanceId]);

    useEffect(() => {
        if (openSignal > 0) setOpen(true);
    }, [openSignal]);

    useEffect(() => {
        if (open) void ensureSession();
    }, [open, ensureSession]);

    useEffect(() => {
        const openDrawer = () => setOpen(true);
        window.addEventListener('hub-open-drawer', openDrawer);
        return () => window.removeEventListener('hub-open-drawer', openDrawer);
    }, []);

    useEffect(() => {
        const onMessage = (event: MessageEvent) => {
            if (event.data?.type === 'hub-widget-unread') {
                setUnread(Math.max(0, Number(event.data.count) || 0));
            }
            if (event.data?.type === 'hub-widget-ready') {
                postClients();
            }
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, [postClients]);

    useEffect(() => {
        postClients();
    }, [postClients, iframeSrc]);

    const switchDisplayMode = (mode: DisplayMode) => {
        setDisplayMode(mode);
        try { localStorage.setItem(DISPLAY_MODE_KEY, mode); } catch { /* ignore */ }
    };

    const handleMiniResize = useCallback((event: ReactMouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        const startX = event.clientX;
        const startY = event.clientY;
        const startW = miniSize.w;
        const startH = miniSize.h;
        const move = (ev: MouseEvent) => {
            ev.preventDefault();
            const maxW = Math.min(MINI_MAX_W, Math.max(MINI_MIN_W, window.innerWidth - 24));
            const maxH = Math.min(MINI_MAX_H, Math.max(MINI_MIN_H, window.innerHeight - 24));
            // Match the RTL mini-panel in AiAssistantDrawer: drag the handle
            // right to grow width, up to grow height.
            setMiniSize({
                w: Math.min(maxW, Math.max(MINI_MIN_W, startW + (ev.clientX - startX))),
                h: Math.min(maxH, Math.max(MINI_MIN_H, startH + (startY - ev.clientY))),
            });
        };
        const up = () => {
            window.removeEventListener('mousemove', move);
            window.removeEventListener('mouseup', up);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            setIsDragging(false);
            setMiniSize((size) => {
                try {
                    localStorage.setItem(MINI_W_KEY, String(size.w));
                    localStorage.setItem(MINI_H_KEY, String(size.h));
                } catch { /* ignore */ }
                return size;
            });
        };
        document.body.style.cursor = 'nw-resize';
        document.body.style.userSelect = 'none';
        setIsDragging(true);
        window.addEventListener('mousemove', move);
        window.addEventListener('mouseup', up);
    }, [miniSize]);

    const handleDrawerResize = useCallback((event: ReactMouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        const startX = event.clientX;
        const startW = drawerWidth;
        const move = (ev: MouseEvent) => {
            ev.preventDefault();
            setDrawerWidth(Math.min(DRAWER_MAX_W, Math.max(DRAWER_MIN_W, startW + (startX - ev.clientX))));
        };
        const up = () => {
            window.removeEventListener('mousemove', move);
            window.removeEventListener('mouseup', up);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            setIsDragging(false);
            setDrawerWidth((width) => {
                try { localStorage.setItem(DRAWER_W_KEY, String(width)); } catch { /* ignore */ }
                return width;
            });
        };
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        setIsDragging(true);
        window.addEventListener('mousemove', move);
        window.addEventListener('mouseup', up);
    }, [drawerWidth]);

    const header = (
        <Box sx={{
            px: 1.25, py: 0.75, backgroundColor: '#00a884', color: '#fff',
            display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0,
        }}>
            <WhatsAppIcon sx={{ fontSize: 20 }} />
            <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 700 }}>שיחות</Typography>
            <Tooltip title="אפליקציית Hub המלאה">
                <IconButton size="small" sx={{ color: '#fff' }} onClick={() => window.open(appUrl, '_blank', 'noopener,noreferrer')}>
                    <OpenInNewIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </Tooltip>
            {displayMode === 'mini' ? (
                <Tooltip title="הרחב">
                    <IconButton size="small" sx={{ color: '#fff' }} onClick={() => switchDisplayMode('drawer')}>
                        <OpenInFullIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Tooltip>
            ) : (
                <Tooltip title="חלון קטן">
                    <IconButton size="small" sx={{ color: '#fff' }} onClick={() => switchDisplayMode('mini')}>
                        <CloseFullscreenIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Tooltip>
            )}
            <IconButton size="small" sx={{ color: '#fff' }} onClick={() => setOpen(false)}>
                <CloseIcon fontSize="small" />
            </IconButton>
        </Box>
    );

    const body = (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', backgroundColor: '#fff', position: 'relative' }}>
            {isDragging ? (
                <Box sx={{
                    position: 'absolute', inset: 0, zIndex: 15,
                    cursor: displayMode === 'mini' ? 'nw-resize' : 'col-resize',
                }} />
            ) : null}
            {error ? (
                <Box sx={{ p: 2, color: 'error.main', fontSize: 13 }}>{error}</Box>
            ) : null}
            {iframeSrc ? (
                <iframe
                    ref={iframeRef}
                    title="Hub conversations"
                    src={iframeSrc}
                    style={{ width: '100%', height: '100%', border: 'none', flex: 1, minHeight: 0, display: 'block' }}
                />
            ) : (
                <Box sx={{ p: 2, color: 'text.secondary', fontSize: 13 }}>טוען שיחות…</Box>
            )}
        </Box>
    );

    const fab = !open ? (
        <Box
            onClick={() => setOpen(true)}
            sx={{
                position: 'fixed',
                bottom: 20,
                right: 20,
                zIndex: 1300,
                width: 56,
                height: 56,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 4px 20px rgba(37,211,102,0.4)',
                '&:hover': { transform: 'scale(1.08)' },
            }}
        >
            <WhatsAppIcon sx={{ color: '#fff', fontSize: 28 }} />
            {unread > 0 ? (
                <Box sx={{
                    position: 'absolute', top: -2, right: -2, minWidth: 20, height: 20, px: 0.5,
                    borderRadius: 10, bgcolor: '#ea4335', color: '#fff', fontSize: 11, fontWeight: 700,
                    display: 'grid', placeItems: 'center',
                }}>
                    {unread > 99 ? '99+' : unread}
                </Box>
            ) : null}
        </Box>
    ) : null;

    if (displayMode === 'mini') {
        return (
            <>
                {open && (
                    <Paper
                        elevation={16}
                        sx={{
                            position: 'fixed',
                            bottom: 16,
                            right: 16,
                            width: `min(${miniSize.w}px, calc(100vw - 24px))`,
                            height: `min(${miniSize.h}px, calc(100vh - 24px))`,
                            zIndex: 1300,
                            borderRadius: 3,
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            minWidth: 0,
                            minHeight: 0,
                        }}
                    >
                        <Box
                            onMouseDown={handleMiniResize}
                            sx={{
                                position: 'absolute', top: 0, left: 0, width: 28, height: 28,
                                cursor: 'nw-resize', zIndex: 21,
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 6,
                                    left: 6,
                                    width: 10,
                                    height: 10,
                                    borderTop: '2px solid rgba(255,255,255,0.85)',
                                    borderLeft: '2px solid rgba(255,255,255,0.85)',
                                },
                            }}
                        />
                        {header}
                        {body}
                    </Paper>
                )}
                {fab}
            </>
        );
    }

    return (
        <>
            {open && (
                <Paper
                    elevation={16}
                    sx={{
                        position: 'fixed',
                        top: 0,
                        right: 0,
                        width: `min(${drawerWidth}px, 100vw)`,
                        height: '100vh',
                        zIndex: 1300,
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: 0,
                        overflow: 'visible',
                    }}
                >
                    <Box
                        onMouseDown={handleDrawerResize}
                        sx={{
                            position: 'absolute',
                            left: -6,
                            top: 0,
                            bottom: 0,
                            width: 12,
                            cursor: 'col-resize',
                            zIndex: 20,
                            display: { xs: 'none', sm: 'flex' },
                            alignItems: 'center',
                            justifyContent: 'center',
                            '&:hover .hub-drag-line': { opacity: 1 },
                        }}
                    >
                        <Box
                            className="hub-drag-line"
                            sx={{
                                width: 4,
                                height: 48,
                                borderRadius: 2,
                                bgcolor: 'rgba(0,0,0,0.18)',
                                opacity: 0.45,
                            }}
                        />
                    </Box>
                    {header}
                    {body}
                </Paper>
            )}
            {fab}
        </>
    );
}
